import { SelectHTMLAttributes, forwardRef } from "react";
import styled from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { FieldFrame, controlStyle, useFieldId } from "@pages/AdminDashboard/ui/FieldFrame";

// The design pads a select's text 10px at each side, 2px under a text control.
const selectPadding = 10;

const Control = styled.select<{ $width?: number; $isPhone: boolean }>`
  ${controlStyle}
  padding: 0 ${selectPadding}px;
  font-family: ${adminTokens.typeScale.body.fontFamily};
`;

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "width"> {
  label: string;
  options: SelectOption[];
  isLabelHidden?: boolean;
  error?: string;
  width?: number;
}

// A labelled native select that looks like the other fields (FIELD-R13). Like the
// text field it hands its ref, name, change and blur to the native element.
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, options, isLabelHidden, error, width, id, ...selectProps }, ref) => {
    const isPhone = useIsPhoneLayout();
    const generatedId = useFieldId();
    const controlId = id ?? generatedId;
    const errorId = `${controlId}-error`;

    return (
      <FieldFrame
        label={label}
        isLabelHidden={isLabelHidden}
        controlId={controlId}
        errorId={errorId}
        error={error}
      >
        <Control
          {...selectProps}
          ref={ref}
          id={controlId}
          $width={width}
          $isPhone={isPhone}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Control>
      </FieldFrame>
    );
  }
);

Select.displayName = "Select";
